// Permission checking utility functions for V2 Delivery Management System

import { UserPermissions } from '../types/auth.types';

// TEMPORARY: Set to true to bypass all permission checks (grant full access)
const BYPASS_PERMISSIONS = true;

/**
 * Check if user has specific permission
 * @param permissionName - The permission name to check (e.g., 'v2.menu.delivery.management')
 * @param operation - The operation type ('view', 'add', 'update', 'delete')
 * @param userPermissions - User permissions object from auth context
 * @returns boolean - True if user has permission, false otherwise
 */
export const hasPermission = (
  permissionName: string,
  operation: 'view' | 'add' | 'update' | 'delete' = 'view',
  userPermissions?: UserPermissions
): boolean => {
  if (BYPASS_PERMISSIONS) {
    return true;
  }

  if (!userPermissions) {
    return false;
  }

  const permission = userPermissions[permissionName];
  if (!permission) {
    return false;
  }

  return permission[operation] === '1';
};

/**
 * Check if user has any of the specified permissions
 * @param permissionNames - Array of permission names to check
 * @param operation - The operation type
 * @param userPermissions - User permissions object
 * @returns boolean - True if user has any of the permissions
 */
export const hasAnyPermission = (
  permissionNames: string[],
  operation: 'view' | 'add' | 'update' | 'delete' = 'view',
  userPermissions?: UserPermissions
): boolean => {
  if (!userPermissions || !permissionNames.length) {
    return false;
  }

  return permissionNames.some(permissionName => 
    hasPermission(permissionName, operation, userPermissions)
  );
};

/**
 * Check if user has all of the specified permissions
 * @param permissionNames - Array of permission names to check
 * @param operation - The operation type
 * @param userPermissions - User permissions object
 * @returns boolean - True if user has all permissions
 */
export const hasAllPermissions = (
  permissionNames: string[],
  operation: 'view' | 'add' | 'update' | 'delete' = 'view',
  userPermissions?: UserPermissions
): boolean => {
  if (!userPermissions || !permissionNames.length) {
    return false;
  }

  return permissionNames.every(permissionName => 
    hasPermission(permissionName, operation, userPermissions)
  );
};

/**
 * Get filtered menu items based on user permissions
 * @param menuItems - Array of menu items with permission requirements
 * @param userPermissions - User permissions object
 * @returns Array - Filtered menu items
 */
export const filterMenuByPermissions = (
  menuItems: any[],
  userPermissions?: UserPermissions
): any[] => {
  if (!userPermissions) {
    return [];
  }

  return menuItems.filter(item => {
    // Check if item has permission requirement
    if (item.permission) {
      return hasPermission(item.permission, 'view', userPermissions);
    }

    // If item has children, filter them recursively
    if (item.children && item.children.length > 0) {
      const filteredChildren = filterMenuByPermissions(item.children, userPermissions);
      item.children = filteredChildren;
      return filteredChildren.length > 0; // Only show parent if it has visible children
    }

    // If no permission requirement, show the item
    return true;
  });
};

/**
 * Permission constants for easy reference
 */
export const PERMISSIONS = {
  // Module Level Permissions (Menu Access & Module Visibility)
  ACCESS_CONTROL: 'v2.access.control',
  DELIVERY_MANAGEMENT: 'v2.delivery.management',
  WAREHOUSE_MANAGEMENT: 'v2.warehouse.management',
  CYCLE_COUNTING: 'v2.cycle.counting',
  CRM_MANAGEMENT: 'v2.crm.management',
  PURCHASE_MANAGEMENT: 'v2.purchase.management',
  CREDIT_MANAGEMENT: 'v2.credit.management',
  SALES_ORDERS: 'v2.sales.orders',
  SALES_QUOTATIONS: 'v2.sales.quotations',
  // POS-terminal (Plan B). The POS server checks the user's REAL role for these (not the bypass).
  POS_SELL: 'v2.pos.sell',
  POS_MANAGE: 'v2.pos.manage',

  // Access Control Permissions
  ACCESS_CONTROL_USERS: 'v2.access.control.users',
  ACCESS_CONTROL_ROLES: 'v2.access.control.roles',
  ACCESS_CONTROL_PERMISSIONS: 'v2.access.control.permissions',
  ACCESS_CONTROL_SYSTEM_SETTINGS: 'v2.access.control.system.settings',
  ACCESS_CONTROL_USER_HISTORY: 'v2.access.control.user.history',
  ACCESS_CONTROL_AUDIT_LOGS: 'v2.access.control.audit.logs',

  // Delivery Management Permissions
  DELIVERY_DASHBOARD: 'v2.delivery.dashboard',
  DELIVERY_TOG_RECEIVED: 'v2.delivery.tog.received',
  DELIVERY_TOG_REQUESTS: 'v2.delivery.tog.requests',
  DELIVERY_TOG_ADD: 'v2.delivery.tog.add',
  DELIVERY_PACKING_TOG_PACKING: 'v2.delivery.packing.tog.packing',
  DELIVERY_PACKING_TOG_UNPACKING: 'v2.delivery.packing.tog.unpacking',
  DELIVERY_SHIPMENT_OUTBOUND: 'v2.delivery.shipment.outbound',
  DELIVERY_SHIPMENT_LOADING: 'v2.delivery.shipment.loading',
  DELIVERY_SHIPMENT_INBOUND: 'v2.delivery.shipment.inbound',
  DELIVERY_TOG_REPORTS: 'v2.delivery.tog.reports',

  // Warehouse Management Permissions
  WAREHOUSE_LOYALTY_OVERVIEW: 'v2.warehouse.loyalty.overview',
  WAREHOUSE_LOYALTY_CUSTOMERS: 'v2.warehouse.loyalty.customers',
  WAREHOUSE_SALES_WEB: 'v2.warehouse.sales.web',
  WAREHOUSE_SALES_POS: 'v2.warehouse.sales.pos',
  WAREHOUSE_REORDER_REPORT: 'v2.warehouse.reorder.report',
  WAREHOUSE_REORDER_ARRIVALS: 'v2.warehouse.reorder.arrivals',
  WAREHOUSE_REORDER_SETTINGS: 'v2.warehouse.reorder.settings',
  WAREHOUSE_FAST_MOVING: 'v2.warehouse.fast.moving',
  WAREHOUSE_MIS_REPORTS: 'v2.warehouse.mis.reports',
  WAREHOUSE_SALES_REPORT_OVERALL: 'v2.warehouse.sales.report.overall',
  WAREHOUSE_SALES_REPORT_PUBLISHER: 'v2.warehouse.sales.report.publisher',

  // Cycle Counting Permissions
  CYCLE_COUNTS: 'v2.cycle.counts',
  CYCLE_PERIODS: 'v2.cycle.periods',
  CYCLE_STOCK_TAKING: 'v2.cycle.stock.taking',
  CYCLE_STOCK_ADJUSTMENT: 'v2.cycle.stock.adjustment',
  CYCLE_RETAKE_SCAN: 'v2.cycle.retake.scan',
  CYCLE_ADMIN: 'v2.cycle.admin',
  CYCLE_REPORTS: 'v2.cycle.reports',

  // Credit Management Permissions
  CREDIT_PAYMENTS_VIEW: 'v2.credit.payments.view',
  CREDIT_PAYMENTS_ADD: 'v2.credit.payments.add',

  // Inventory Management Permissions
  INVENTORY_ITEMS: 'v2.inventory.item',
  INVENTORY_PACKS: 'v2.inventory.pack',
  INVENTORY_PRICE_HISTORY: 'v2.inventory.price_history',
  INVENTORY_ENABLE_DISABLE: 'v2.inventory.enable_disable',
  INVENTORY_MASTER_DATA: 'v2.inventory.category',
  INVENTORY_PUBLISHER: 'v2.inventory.publisher',
  INVENTORY_AUTHOR: 'v2.inventory.author',
  INVENTORY_BULK_FETCH: 'v2.inventory.bulk_fetch',
  INVENTORY_RACK: 'v2.inventory.rack',
  INVENTORY_TAG: 'v2.inventory.tag',
  INVENTORY_CURRENCY: 'v2.inventory.currency',
  INVENTORY_LANGUAGE: 'v2.inventory.language',
  INVENTORY_REGION: 'v2.inventory.region',
  INVENTORY_GROUP: 'v2.inventory.group',
} as const;

/**
 * Permission groups for organizing related permissions
 */
export const PERMISSION_GROUPS = {
  ACCESS_CONTROL: [
    PERMISSIONS.ACCESS_CONTROL,
    PERMISSIONS.ACCESS_CONTROL_USERS,
    PERMISSIONS.ACCESS_CONTROL_ROLES,
    PERMISSIONS.ACCESS_CONTROL_PERMISSIONS,
    PERMISSIONS.ACCESS_CONTROL_SYSTEM_SETTINGS,
    PERMISSIONS.ACCESS_CONTROL_USER_HISTORY,
    PERMISSIONS.ACCESS_CONTROL_AUDIT_LOGS,
  ],
  DELIVERY: [
    PERMISSIONS.DELIVERY_MANAGEMENT,
    PERMISSIONS.DELIVERY_DASHBOARD,
    PERMISSIONS.DELIVERY_TOG_RECEIVED,
    PERMISSIONS.DELIVERY_TOG_REQUESTS,
    PERMISSIONS.DELIVERY_TOG_ADD,
    PERMISSIONS.DELIVERY_PACKING_TOG_PACKING,
    PERMISSIONS.DELIVERY_PACKING_TOG_UNPACKING,
    PERMISSIONS.DELIVERY_SHIPMENT_OUTBOUND,
    PERMISSIONS.DELIVERY_SHIPMENT_LOADING,
    PERMISSIONS.DELIVERY_SHIPMENT_INBOUND,
    PERMISSIONS.DELIVERY_TOG_REPORTS,
  ],
  WAREHOUSE: [
    PERMISSIONS.WAREHOUSE_MANAGEMENT,
    PERMISSIONS.WAREHOUSE_LOYALTY_OVERVIEW,
    PERMISSIONS.WAREHOUSE_LOYALTY_CUSTOMERS,
    PERMISSIONS.WAREHOUSE_SALES_WEB,
    PERMISSIONS.WAREHOUSE_SALES_POS,
    PERMISSIONS.WAREHOUSE_REORDER_REPORT,
    PERMISSIONS.WAREHOUSE_REORDER_ARRIVALS,
    PERMISSIONS.WAREHOUSE_REORDER_SETTINGS,
    PERMISSIONS.WAREHOUSE_FAST_MOVING,
    PERMISSIONS.WAREHOUSE_MIS_REPORTS,
  ],
  CYCLE_COUNTING: [
    PERMISSIONS.CYCLE_COUNTING,
    PERMISSIONS.CYCLE_COUNTS,
    PERMISSIONS.CYCLE_PERIODS,
    PERMISSIONS.CYCLE_STOCK_TAKING,
    PERMISSIONS.CYCLE_STOCK_ADJUSTMENT,
    PERMISSIONS.CYCLE_RETAKE_SCAN,
    PERMISSIONS.CYCLE_ADMIN,
    PERMISSIONS.CYCLE_REPORTS,
  ],
  CRM: [
    PERMISSIONS.CRM_MANAGEMENT,
  ],
  PURCHASE: [
    PERMISSIONS.PURCHASE_MANAGEMENT,
  ],
  SALES: [
    PERMISSIONS.SALES_ORDERS,
    PERMISSIONS.SALES_QUOTATIONS,
  ],
  POS: [
    PERMISSIONS.POS_SELL,
    PERMISSIONS.POS_MANAGE,
  ],
  CREDIT: [
    PERMISSIONS.CREDIT_MANAGEMENT,
    PERMISSIONS.CREDIT_PAYMENTS_VIEW,
    PERMISSIONS.CREDIT_PAYMENTS_ADD,
  ],
  INVENTORY: [
    PERMISSIONS.INVENTORY_ITEMS,
    PERMISSIONS.INVENTORY_PACKS,
    PERMISSIONS.INVENTORY_PRICE_HISTORY,
    PERMISSIONS.INVENTORY_ENABLE_DISABLE,
    PERMISSIONS.INVENTORY_MASTER_DATA,
    PERMISSIONS.INVENTORY_PUBLISHER,
    PERMISSIONS.INVENTORY_AUTHOR,
    PERMISSIONS.INVENTORY_BULK_FETCH,
    PERMISSIONS.INVENTORY_RACK,
    PERMISSIONS.INVENTORY_TAG,
    PERMISSIONS.INVENTORY_CURRENCY,
    PERMISSIONS.INVENTORY_LANGUAGE,
    PERMISSIONS.INVENTORY_REGION,
    PERMISSIONS.INVENTORY_GROUP,
  ],
} as const;
